import React, { useEffect, useRef, useState } from 'react';
import { GripVertical } from 'lucide-react';

/**
 * ParticipantCameraStrip — small draggable strip of participant cameras
 * shown while a proposal is being presented (so it doesn't get hidden
 * behind the full-screen slide deck).
 *
 * Props:
 *   peersRef:     React ref to MeetingRoom's Map<peer_id, {peer, role, name, stream, videoEl}>
 *   remotePeers:  state array (peer_id, role, name, hasStream) — drives re-renders
 *   testId:       optional data-testid for the wrapper
 *
 * Notes:
 *  - Each tile mounts a <video> via a ref callback that wires srcObject from
 *    the corresponding peersRef entry. We re-use the existing PeerConnection
 *    instances — no second stream is requested.
 *  - The strip is draggable end-to-end via the small grip handle so brokers
 *    can park it wherever it doesn't cover slide content.
 *  - Snaps to corner on double-click (matches DraggablePiP behaviour for
 *    consistency).
 */
const ParticipantCameraStrip = ({ peersRef, remotePeers, testId = 'participant-strip' }) => {
  const [pos, setPos] = useState({ x: window.innerWidth - 196, y: 80 });
  const dragRef = useRef({ active: false, dx: 0, dy: 0 });

  // Re-clamp on window resize so the strip doesn't end up off-screen.
  useEffect(() => {
    const onResize = () => {
      setPos((p) => ({
        x: Math.min(p.x, window.innerWidth - 200),
        y: Math.min(p.y, window.innerHeight - 200),
      }));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onPointerDown = (e) => {
    if (e.button === 2) return;
    dragRef.current = {
      active: true,
      dx: e.clientX - pos.x,
      dy: e.clientY - pos.y,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current.active) return;
    setPos({
      x: Math.max(8, Math.min(window.innerWidth - 184, e.clientX - dragRef.current.dx)),
      y: Math.max(8, Math.min(window.innerHeight - 200, e.clientY - dragRef.current.dy)),
    });
  };
  const onPointerUp = () => {
    dragRef.current.active = false;
  };
  const snapToCorner = () => {
    // Cycle: top-right → bottom-right → bottom-left → top-left → top-right
    const w = 176;
    const corners = [
      { x: window.innerWidth - w - 16, y: 80 },                          // top-right
      { x: window.innerWidth - w - 16, y: window.innerHeight - 280 },    // bottom-right
      { x: 16, y: window.innerHeight - 280 },                            // bottom-left
      { x: 16, y: 80 },                                                  // top-left
    ];
    const idx = corners.findIndex(
      (c) => Math.abs(c.x - pos.x) < 24 && Math.abs(c.y - pos.y) < 24
    );
    const next = corners[(idx + 1) % corners.length];
    setPos(next);
  };

  if (!remotePeers || !remotePeers.length) return null;

  return (
    <div
      style={{ left: pos.x, top: pos.y }}
      className="fixed z-[55] pointer-events-auto select-none"
      data-testid={testId}
    >
      <div className="bg-slate-900/95 backdrop-blur-md border border-white/10 rounded-xl shadow-2xl overflow-hidden">
        {/* Drag handle */}
        <div
          className="flex items-center justify-between px-2 py-1 bg-slate-800/80 cursor-move"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onDoubleClick={snapToCorner}
          data-testid={`${testId}-handle`}
        >
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-300">
            <GripVertical className="h-3 w-3" />
            <span>{remotePeers.length} participant{remotePeers.length === 1 ? '' : 's'}</span>
          </div>
        </div>

        {/* Tiles */}
        <div className="grid grid-cols-1 gap-px p-px bg-slate-700/30 max-h-[60vh] overflow-y-auto">
          {remotePeers.map((p) => (
            <div key={p.peer_id} className="relative bg-slate-950" style={{ width: 174, height: 100 }}>
              <video
                autoPlay
                playsInline
                className="w-full h-full object-cover"
                data-testid={`participant-strip-video-${p.peer_id}`}
                ref={(el) => {
                  const entry = peersRef.current.get(p.peer_id);
                  if (!entry) return;
                  // We allow MeetingRoom's main remote-grid <video> to remain
                  // the canonical one. The strip mounts a SECOND <video>
                  // pointing at the same srcObject; the browser de-dupes
                  // decoding internally so cost is minimal.
                  if (el && entry.stream && el.srcObject !== entry.stream) {
                    el.srcObject = entry.stream;
                    const pp = el.play?.();
                    if (pp && typeof pp.catch === 'function') pp.catch(() => {});
                  }
                }}
              />
              {(p.name || p.role) && (
                <div className="absolute bottom-1 left-1 right-1 text-[10px] font-medium bg-slate-900/70 backdrop-blur text-white px-1.5 py-0.5 rounded truncate">
                  {p.name || (p.role === 'host' ? 'Broker' : 'Guest')}
                </div>
              )}
              {!p.hasStream && (
                <div className="absolute inset-0 flex items-center justify-center text-[10px] text-slate-500">
                  Connecting…
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ParticipantCameraStrip;
